import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Loader2, Ticket, Users, Calendar, Mail, Phone } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Organizer-safe ticket sales panel. Shows per-tier roll-up + unique-buyer
 * contact rows for a specific event.
 *
 * STRICT rule: the component never surfaces platform/convenience fees, GST,
 * or any Unight commission — it only displays what the server returns, and
 * the server has already redacted those fields at the API layer.
 */
export default function TicketSalesDialog({ open, onOpenChange, event, token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dateFilter, setDateFilter] = useState('');  // '' = all dates
  const [search, setSearch] = useState('');

  const authHeaders = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    if (!event?.id || !token) return;
    setLoading(true);
    try {
      const params = {};
      if (dateFilter) params.date_id = dateFilter;
      const res = await axios.get(
        `${API}/organizer/events/${event.id}/ticket-sales`,
        { headers: authHeaders, params },
      );
      setData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load sales');
      setData(null);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event?.id, token, dateFilter]);

  useEffect(() => {
    if (open) {
      setDateFilter('');
      setSearch('');
      load();
    }
  }, [open, load]);

  // Re-fetch when the date filter flips
  useEffect(() => { if (open) load(); }, [dateFilter, load, open]);

  const filteredBuyers = (data?.buyers || []).filter((b) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (b.name || '').toLowerCase().includes(q)
      || (b.email || '').toLowerCase().includes(q)
      || (b.phone || '').toLowerCase().includes(q);
  });

  const copy = async (val) => {
    try {
      await navigator.clipboard.writeText(val);
      toast.success('Copied');
    } catch {
      /* noop */
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[#0A0A0A] border-zinc-800 text-white w-[calc(100vw-1.5rem)] sm:max-w-4xl max-h-[90vh] overflow-y-auto p-4 sm:p-6"
        data-testid="ticket-sales-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-['Josefin_Sans'] text-xl flex items-center gap-2">
            <Ticket size={20} className="text-[#B5FF00]" /> TICKET SALES
          </DialogTitle>
          <p className="text-zinc-400 text-xs mt-1 font-mono">
            Event: <span className="text-white">{event?.title}</span>
          </p>
        </DialogHeader>

        {loading && (
          <div className="flex items-center justify-center py-12 text-zinc-500 text-xs font-mono">
            <Loader2 size={16} className="animate-spin mr-2" /> LOADING SALES…
          </div>
        )}

        {!loading && data && (
          <div className="space-y-6 mt-2">
            {/* Multi-day date filter */}
            {data.is_multi_day && data.date_options?.length > 0 && (
              <div className="flex flex-wrap gap-2" data-testid="sales-date-filter">
                <button
                  type="button"
                  onClick={() => setDateFilter('')}
                  className={`px-3 py-1.5 border font-mono text-xs tracking-wider ${
                    !dateFilter
                      ? 'border-[#B5FF00] bg-[#B5FF00]/10 text-[#B5FF00]'
                      : 'border-zinc-800 text-zinc-400 hover:border-zinc-600'
                  }`}
                  data-testid="sales-filter-all"
                >
                  ALL DATES
                </button>
                {data.date_options.map((d) => (
                  <button
                    key={d.id}
                    type="button"
                    onClick={() => setDateFilter(d.id)}
                    className={`px-3 py-1.5 border font-mono text-xs tracking-wider ${
                      dateFilter === d.id
                        ? 'border-[#B5FF00] bg-[#B5FF00]/10 text-[#B5FF00]'
                        : 'border-zinc-800 text-zinc-400 hover:border-zinc-600'
                    }`}
                    data-testid={`sales-filter-date-${d.id}`}
                  >
                    {(d.label || d.date || '').toUpperCase()}
                  </button>
                ))}
              </div>
            )}

            {/* Totals strip */}
            <div className="grid grid-cols-3 gap-3" data-testid="sales-totals">
              <div className="border border-zinc-800 bg-[#050505] p-4">
                <div className="font-mono text-[10px] text-zinc-500 tracking-widest">TICKETS SOLD</div>
                <div className="text-3xl text-white font-['Josefin_Sans'] mt-1">{data.totals.tickets_sold}</div>
              </div>
              <div className="border border-zinc-800 bg-[#050505] p-4">
                <div className="font-mono text-[10px] text-zinc-500 tracking-widest">UNIQUE BUYERS</div>
                <div className="text-3xl text-white font-['Josefin_Sans'] mt-1">{data.totals.unique_buyers}</div>
              </div>
              <div className="border border-zinc-800 bg-[#050505] p-4">
                <div className="font-mono text-[10px] text-zinc-500 tracking-widest">GROSS SALES</div>
                <div className="text-3xl text-[#B5FF00] font-['Josefin_Sans'] mt-1">
                  ₹{Number(data.totals.face_value_total || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                </div>
                <div className="font-mono text-[9px] text-zinc-500 mt-1 tracking-wider">TICKET FACE VALUE</div>
              </div>
            </div>

            {/* Per-tier breakdown */}
            <section data-testid="sales-tier-section">
              <h3 className="font-mono text-xs text-zinc-400 tracking-widest mb-2">BY TICKET TYPE</h3>
              {data.tiers.length === 0 ? (
                <p className="text-zinc-500 text-xs">No tickets sold yet{dateFilter ? ' for the selected date' : ''}.</p>
              ) : (
                <div className="border border-zinc-800">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-zinc-800 bg-[#050505]">
                        <th className="text-left py-2 px-3 font-mono text-[10px] text-zinc-500 tracking-widest">TICKET TYPE</th>
                        <th className="text-right py-2 px-3 font-mono text-[10px] text-zinc-500 tracking-widest">PRICE</th>
                        <th className="text-right py-2 px-3 font-mono text-[10px] text-zinc-500 tracking-widest">SOLD</th>
                        <th className="text-right py-2 px-3 font-mono text-[10px] text-zinc-500 tracking-widest">REMAINING</th>
                        <th className="text-right py-2 px-3 font-mono text-[10px] text-zinc-500 tracking-widest">GROSS</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.tiers.map((t, i) => (
                        <tr key={t.tier_id || i} className="border-b border-zinc-900" data-testid={`sales-tier-row-${t.tier_id || i}`}>
                          <td className="py-2 px-3 text-white font-['Josefin_Sans']">{t.tier_name}</td>
                          <td className="py-2 px-3 text-right text-zinc-300 font-mono text-xs">
                            {t.unit_price > 0 ? `₹${t.unit_price.toLocaleString('en-IN')}` : 'FREE'}
                          </td>
                          <td className="py-2 px-3 text-right text-[#B5FF00] font-mono">{t.tickets_sold}</td>
                          <td className="py-2 px-3 text-right text-zinc-400 font-mono text-xs">{t.inventory_remaining}</td>
                          <td className="py-2 px-3 text-right text-white font-mono">
                            ₹{Number(t.face_value_total).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            {/* Buyer rows */}
            <section data-testid="sales-buyers-section">
              <div className="flex items-center justify-between mb-2 gap-3">
                <h3 className="font-mono text-xs text-zinc-400 tracking-widest flex items-center gap-2">
                  <Users size={12} /> BUYERS · {filteredBuyers.length}
                </h3>
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search name / email / phone…"
                  className="h-9 bg-[#050505] border border-zinc-800 text-white px-3 text-sm w-56 focus:border-[#B5FF00] focus:outline-none"
                  data-testid="sales-buyers-search"
                />
              </div>

              {filteredBuyers.length === 0 ? (
                <p className="text-zinc-500 text-xs">No buyers yet{dateFilter ? ' for the selected date' : ''}.</p>
              ) : (
                <div className="border border-zinc-800 overflow-x-auto">
                  <table className="w-full text-sm min-w-[640px]">
                    <thead>
                      <tr className="border-b border-zinc-800 bg-[#050505]">
                        <th className="text-left py-2 px-3 font-mono text-[10px] text-zinc-500 tracking-widest">NAME</th>
                        <th className="text-left py-2 px-3 font-mono text-[10px] text-zinc-500 tracking-widest">EMAIL</th>
                        <th className="text-left py-2 px-3 font-mono text-[10px] text-zinc-500 tracking-widest">PHONE</th>
                        <th className="text-right py-2 px-3 font-mono text-[10px] text-zinc-500 tracking-widest">TICKETS</th>
                        <th className="text-left py-2 px-3 font-mono text-[10px] text-zinc-500 tracking-widest">TIERS</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredBuyers.map((b, i) => (
                        <tr key={`${b.email}-${i}`} className="border-b border-zinc-900 hover:bg-[#050505]" data-testid={`sales-buyer-row-${i}`}>
                          <td className="py-2 px-3 text-white font-['Josefin_Sans']">{b.name || '—'}</td>
                          <td className="py-2 px-3 text-zinc-300 font-mono text-xs">
                            <button
                              type="button"
                              onClick={() => copy(b.email)}
                              className="flex items-center gap-1 hover:text-[#B5FF00] max-w-[200px] truncate"
                              title="Copy email"
                            >
                              <Mail size={11} /> <span className="truncate">{b.email || '—'}</span>
                            </button>
                          </td>
                          <td className="py-2 px-3 text-zinc-300 font-mono text-xs">
                            <button
                              type="button"
                              onClick={() => copy(b.phone)}
                              className="flex items-center gap-1 hover:text-[#B5FF00]"
                              title="Copy phone"
                            >
                              <Phone size={11} /> {b.phone || '—'}
                            </button>
                          </td>
                          <td className="py-2 px-3 text-right text-[#B5FF00] font-mono">{b.ticket_count}</td>
                          <td className="py-2 px-3 text-zinc-400 font-mono text-[11px]">
                            <div className="flex flex-wrap gap-1">
                              {(b.tiers || []).map((n) => (
                                <span key={n} className="px-1.5 py-0.5 bg-zinc-900 border border-zinc-800 tracking-wider">
                                  {n.toUpperCase()}
                                </span>
                              ))}
                              {b.dates && b.dates.length > 0 && b.dates.map((d) => (
                                <span key={d} className="px-1.5 py-0.5 bg-[#B5FF00]/5 border border-[#B5FF00]/20 text-[#B5FF00] tracking-wider flex items-center gap-1">
                                  <Calendar size={9} />{d}
                                </span>
                              ))}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
